import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Box,
    Typography,
    Tabs,
    Tab,
    Chip,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack,
    TextField,
    Autocomplete,
    Button,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    List,
    ListItemButton,
    ListItemText,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockIcon from "@mui/icons-material/Lock";
import { useAuth } from "../contexts/AuthContext";
import TaskService from "../services/Task.service";
import ProjectService from "../services/Project.service";
import UserService from "../services/User.service";
import AddTaskDialog from "../components/Tasks/AddTaskDialog";
import TaskPreviewDialog from "../components/Tasks/TaskPreviewDialog";
import TodayFocusTab from "../components/Tasks/TodayFocusTab";

// ── helpers ────────────────────────────────────────────────────────────────────

function formatDue(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function isOverdue(dateStr) {
    if (!dateStr) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(dateStr + "T00:00:00") < today;
}

function sortTasks(tasks, sortBy) {
    return [...tasks].sort((a, b) => {
        if (sortBy === "due_asc") {
            if (!a.due_date) return 1;
            if (!b.due_date) return -1;
            return new Date(a.due_date) - new Date(b.due_date);
        }
        if (sortBy === "due_desc") {
            if (!a.due_date) return 1;
            if (!b.due_date) return -1;
            return new Date(b.due_date) - new Date(a.due_date);
        }
        if (sortBy === "title_asc") return a.title.localeCompare(b.title);
        if (sortBy === "title_desc") return b.title.localeCompare(a.title);
        return 0;
    });
}

// ── Task card row ──────────────────────────────────────────────────────────────

function TaskCard({ task, onOpen }) {
    const due = formatDue(task.due_date);
    const overdue = !task.is_complete && isOverdue(task.due_date);
    const borderColor = task.is_public ? "primary.main" : "success.main";

    const projectNum = task.project_detail?.number ?? task.quote_detail?.number ?? null;

    const meta = [
        task.assignee_detail?.full_name,
        task.tasklist_detail?.title,
    ].filter(Boolean).join(" · ");

    return (
        <ListItemButton
            onClick={() => onOpen(task)}
            sx={{
                px: 1.5,
                py: 1,
                border: 1,
                borderColor: "divider",
                borderLeft: 3,
                borderLeftColor: borderColor,
                borderRadius: 1,
                gap: 1,
                alignItems: "flex-start",
                "&:hover": { bgcolor: "action.hover" },
            }}
        >
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                    {task.is_complete && (
                        <CheckCircleIcon sx={{ fontSize: "0.9rem", color: "success.main", flexShrink: 0 }} />
                    )}
                    {!task.is_public && (
                        <LockIcon sx={{ fontSize: "0.75rem", color: "text.disabled", flexShrink: 0 }} />
                    )}
                    <Typography
                        variant="body2"
                        noWrap
                        sx={{
                            fontFamily: "Montserrat, sans-serif",
                            fontWeight: 600,
                            textDecoration: task.is_complete ? "line-through" : "none",
                            color: task.is_complete ? "text.disabled" : "text.primary",
                        }}
                    >
                        {task.title}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }} flexWrap="wrap">
                    {projectNum && (
                        <Typography
                            variant="caption"
                            sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, color: "secondary.main" }}
                        >
                            {projectNum}
                        </Typography>
                    )}
                    {projectNum && meta && <Typography variant="caption" color="text.disabled">·</Typography>}
                    {meta && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "Montserrat, sans-serif" }}>
                            {meta}
                        </Typography>
                    )}
                    {due && (
                        <>
                            {(projectNum || meta) && <Typography variant="caption" color="text.disabled">·</Typography>}
                            <Typography
                                variant="caption"
                                sx={{
                                    fontFamily: "Montserrat, sans-serif",
                                    color: overdue ? "error.main" : "text.secondary",
                                    fontWeight: overdue ? 700 : 400,
                                }}
                            >
                                {due}
                            </Typography>
                        </>
                    )}
                </Stack>
            </Box>
            <ChevronRightIcon sx={{ color: "text.disabled", fontSize: "1.1rem", mt: 0.5, flexShrink: 0 }} />
        </ListItemButton>
    );
}

// ── Shared task list renderer ──────────────────────────────────────────────────

function TaskList({ tasks, sortBy, onOpen, loading, emptyText }) {
    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} />
            </Box>
        );
    }
    const sorted = sortTasks(tasks, sortBy);
    if (sorted.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4, fontFamily: "Montserrat, sans-serif" }}>
                {emptyText}
            </Typography>
        );
    }
    return (
        <Stack spacing={1}>
            {sorted.map((task) => (
                <TaskCard key={task.id} task={task} onOpen={onOpen} />
            ))}
        </Stack>
    );
}

// ── My Tasks tab ──────────────────────────────────────────────────────────────

function MyTasksTab({ currentUser, taskLists, allUsers }) {
    const [sortBy, setSortBy] = useState("due_asc");
    const [selectedListId, setSelectedListId] = useState("");
    const [selectedUser, setSelectedUser] = useState(currentUser);
    const [openTasks, setOpenTasks] = useState([]);
    const [completedTasks, setCompletedTasks] = useState([]);
    const [completedLoaded, setCompletedLoaded] = useState(false);
    const [loadingOpen, setLoadingOpen] = useState(false);
    const [loadingCompleted, setLoadingCompleted] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editTask, setEditTask] = useState(null);
    const [previewTask, setPreviewTask] = useState(null);

    const isViewingOwn = selectedUser?.id === currentUser.id;

    const fetchOpen = useCallback((userId, listId) => {
        setLoadingOpen(true);
        const params = { assignee: userId };
        if (listId) params.tasklist = listId;
        TaskService.list(params)
            .then((res) => setOpenTasks(res.data.results ?? res.data))
            .catch(() => setOpenTasks([]))
            .finally(() => setLoadingOpen(false));
    }, []);

    useEffect(() => {
        fetchOpen(currentUser.id, null);
    }, [currentUser.id, fetchOpen]);

    const handleUserChange = (_, value) => {
        const u = value ?? currentUser;
        setSelectedUser(u);
        setSelectedListId("");
        setCompletedLoaded(false);
        setCompletedTasks([]);
        fetchOpen(u.id, null);
    };

    const handleListChange = (e) => {
        const val = e.target.value;
        setSelectedListId(val);
        setCompletedLoaded(false);
        setCompletedTasks([]);
        fetchOpen(selectedUser.id, val || null);
    };

    const loadCompleted = () => {
        if (completedLoaded) return;
        setLoadingCompleted(true);
        const params = { assignee: selectedUser.id, is_complete: "true", page_size: 50 };
        if (selectedListId) params.tasklist = selectedListId;
        TaskService.list(params)
            .then((res) => { setCompletedTasks(res.data.results ?? res.data); setCompletedLoaded(true); })
            .catch(() => setCompletedTasks([]))
            .finally(() => setLoadingCompleted(false));
    };

    const handleTaskUpdated = (updated) => {
        setPreviewTask(updated);
        if (updated.is_complete) {
            setOpenTasks((p) => p.filter((t) => t.id !== updated.id));
            setCompletedTasks((p) => [updated, ...p.filter((t) => t.id !== updated.id)]);
        } else {
            setCompletedTasks((p) => p.filter((t) => t.id !== updated.id));
            setOpenTasks((p) => [updated, ...p.filter((t) => t.id !== updated.id)]);
        }
    };

    const handleEdit = (task) => {
        setPreviewTask(null);
        setEditTask(task);
        setDialogOpen(true);
    };

    const handleSaved = (saved) => {
        if (editTask) {
            setOpenTasks((p) => p.map((t) => t.id === saved.id ? saved : t));
            setCompletedTasks((p) => p.map((t) => t.id === saved.id ? saved : t));
        } else {
            setOpenTasks((p) => [saved, ...p]);
        }
    };

    const handleDeleted = (deleted) => {
        setOpenTasks((p) => p.filter((t) => t.id !== deleted.id));
        setCompletedTasks((p) => p.filter((t) => t.id !== deleted.id));
    };

    return (
        <Box>
            {/* Controls */}
            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel>Sort By</InputLabel>
                    <Select value={sortBy} label="Sort By" onChange={(e) => setSortBy(e.target.value)}>
                        <MenuItem value="due_asc">Due Date (Oldest)</MenuItem>
                        <MenuItem value="due_desc">Due Date (Newest)</MenuItem>
                        <MenuItem value="title_asc">Title A–Z</MenuItem>
                        <MenuItem value="title_desc">Title Z–A</MenuItem>
                    </Select>
                </FormControl>
                {isViewingOwn && (
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<AddIcon />}
                        onClick={() => { setEditTask(null); setDialogOpen(true); }}
                        sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0, height: 40 }}
                    >
                        Add
                    </Button>
                )}
            </Stack>

            {/* Employee picker */}
            <Autocomplete
                options={allUsers}
                getOptionLabel={(u) => u.full_name || `${u.first_name} ${u.last_name}`.trim() || u.username}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={selectedUser}
                onChange={handleUserChange}
                disableClearable
                size="small"
                sx={{ mb: 1.5 }}
                renderInput={(params) => <TextField {...params} label="Employee" />}
                renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                        <Typography variant="body2" sx={{ fontFamily: "Montserrat, sans-serif" }}>
                            {option.full_name || `${option.first_name} ${option.last_name}`.trim() || option.username}
                            {option.id === currentUser.id && (
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>(you)</Typography>
                            )}
                        </Typography>
                    </li>
                )}
            />

            {/* Task list filter */}
            <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                <InputLabel>Task List</InputLabel>
                <Select value={selectedListId} label="Task List" onChange={handleListChange}>
                    <MenuItem value=""><em>All Lists</em></MenuItem>
                    {taskLists.map((tl) => (
                        <MenuItem key={tl.id} value={String(tl.id)}>
                            {tl.title}
                            {isViewingOwn && tl.task_count > 0 && (
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    ({tl.task_count})
                                </Typography>
                            )}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {!isViewingOwn && (
                <Chip
                    label={`Showing public tasks for ${selectedUser.first_name || selectedUser.username}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ mb: 1.5, fontFamily: "Montserrat, sans-serif" }}
                />
            )}

            {/* Open tasks */}
            <TaskList
                tasks={openTasks}
                sortBy={sortBy}
                onOpen={setPreviewTask}
                loading={loadingOpen}
                emptyText="No open tasks"
            />

            {/* Completed */}
            <Accordion
                disableGutters
                onChange={(_, expanded) => expanded && loadCompleted()}
                sx={{ mt: 2, boxShadow: "none", border: 1, borderColor: "divider", borderRadius: 1, "&:before": { display: "none" } }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2" fontWeight={600} sx={{ fontFamily: "Montserrat, sans-serif" }}>
                        Completed
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    {loadingCompleted ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={22} /></Box>
                    ) : completedTasks.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2, fontFamily: "Montserrat, sans-serif" }}>
                            No completed tasks
                        </Typography>
                    ) : (
                        <Stack spacing={1} sx={{ p: 1 }}>
                            {sortTasks(completedTasks, sortBy).map((task) => (
                                <TaskCard key={task.id} task={task} onOpen={setPreviewTask} />
                            ))}
                        </Stack>
                    )}
                </AccordionDetails>
            </Accordion>

            <TaskPreviewDialog
                task={previewTask}
                onClose={() => setPreviewTask(null)}
                currentUserId={currentUser.id}
                onTaskUpdated={handleTaskUpdated}
                onEdit={handleEdit}
                onDeleted={handleDeleted}
            />

            <AddTaskDialog
                open={dialogOpen}
                onClose={() => { setDialogOpen(false); setEditTask(null); }}
                onSaved={handleSaved}
                currentUser={currentUser}
                defaultProject={null}
                taskLists={taskLists}
                item={editTask}
            />
        </Box>
    );
}

// ── Project Tasks tab ──────────────────────────────────────────────────────────

function ProjectTasksTab({ currentUser, taskLists }) {
    const [project, setProject] = useState(null);
    const [projectOptions, setProjectOptions] = useState([]);
    const [projectSearch, setProjectSearch] = useState("");
    const [projectLoading, setProjectLoading] = useState(false);
    const searchTimer = useRef(null);

    const [sortBy, setSortBy] = useState("due_asc");
    const [openTasks, setOpenTasks] = useState([]);
    const [completedTasks, setCompletedTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editTask, setEditTask] = useState(null);
    const [previewTask, setPreviewTask] = useState(null);

    useEffect(() => {
        clearTimeout(searchTimer.current);
        if (!projectSearch.trim()) { setProjectOptions([]); return; }
        setProjectLoading(true);
        searchTimer.current = setTimeout(() => {
            ProjectService.list({ search: projectSearch.trim(), page_size: 20 })
                .then((res) => setProjectOptions(res.data.results ?? res.data))
                .catch(() => setProjectOptions([]))
                .finally(() => setProjectLoading(false));
        }, 300);
        return () => clearTimeout(searchTimer.current);
    }, [projectSearch]);

    const fetchProjectTasks = useCallback((projectId) => {
        setLoading(true);
        TaskService.list({ project: projectId, include_completed: "true", page_size: 200 })
            .then((res) => {
                const all = res.data.results ?? res.data;
                setOpenTasks(all.filter((t) => !t.is_complete));
                setCompletedTasks(all.filter((t) => t.is_complete));
            })
            .catch(() => { setOpenTasks([]); setCompletedTasks([]); })
            .finally(() => setLoading(false));
    }, []);

    const handleProjectChange = (_, value) => {
        setProject(value);
        if (value?.id) fetchProjectTasks(value.id);
        else { setOpenTasks([]); setCompletedTasks([]); }
    };

    const handleTaskUpdated = (updated) => {
        setPreviewTask(updated);
        if (updated.is_complete) {
            setOpenTasks((p) => p.filter((t) => t.id !== updated.id));
            setCompletedTasks((p) => [updated, ...p.filter((t) => t.id !== updated.id)]);
        } else {
            setCompletedTasks((p) => p.filter((t) => t.id !== updated.id));
            setOpenTasks((p) => [updated, ...p.filter((t) => t.id !== updated.id)]);
        }
    };

    const handleEdit = (task) => {
        setPreviewTask(null);
        setEditTask(task);
        setDialogOpen(true);
    };

    const handleSaved = (saved) => {
        if (editTask) {
            setOpenTasks((p) => p.map((t) => t.id === saved.id ? saved : t));
            setCompletedTasks((p) => p.map((t) => t.id === saved.id ? saved : t));
        } else {
            setOpenTasks((p) => [saved, ...p]);
        }
    };

    const handleDeleted = (deleted) => {
        setOpenTasks((p) => p.filter((t) => t.id !== deleted.id));
        setCompletedTasks((p) => p.filter((t) => t.id !== deleted.id));
    };

    return (
        <Box>
            {/* Project search */}
            <Autocomplete
                options={projectOptions}
                getOptionLabel={(o) => `${o.number} – ${o.name}`}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={project}
                onChange={handleProjectChange}
                onInputChange={(_, val) => setProjectSearch(val)}
                loading={projectLoading}
                noOptionsText={projectSearch.trim().length < 1 ? "Type to search projects" : "No projects found"}
                size="small"
                sx={{ mb: 1.5 }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Search Projects"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {projectLoading && <CircularProgress size={16} sx={{ mr: 1 }} />}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
                renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                        <Typography variant="body2" sx={{ fontFamily: "Montserrat, sans-serif" }}>
                            <strong>{option.number}</strong> – {option.name}
                        </Typography>
                    </li>
                )}
            />

            {project && (
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                        <InputLabel>Sort By</InputLabel>
                        <Select value={sortBy} label="Sort By" onChange={(e) => setSortBy(e.target.value)}>
                            <MenuItem value="due_asc">Due Date (Oldest)</MenuItem>
                            <MenuItem value="due_desc">Due Date (Newest)</MenuItem>
                            <MenuItem value="title_asc">Title A–Z</MenuItem>
                            <MenuItem value="title_desc">Title Z–A</MenuItem>
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<AddIcon />}
                        onClick={() => { setEditTask(null); setDialogOpen(true); }}
                        sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0, height: 40 }}
                    >
                        Add
                    </Button>
                </Stack>
            )}

            {!project ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4, fontFamily: "Montserrat, sans-serif" }}>
                    Select a project to view tasks
                </Typography>
            ) : loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={28} /></Box>
            ) : (
                <>
                    <TaskList
                        tasks={openTasks}
                        sortBy={sortBy}
                        onOpen={setPreviewTask}
                        loading={false}
                        emptyText="No open tasks"
                    />

                    {completedTasks.length > 0 && (
                        <Accordion
                            disableGutters
                            sx={{ mt: 2, boxShadow: "none", border: 1, borderColor: "divider", borderRadius: 1, "&:before": { display: "none" } }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="body2" fontWeight={600} sx={{ fontFamily: "Montserrat, sans-serif" }}>
                                        Completed
                                    </Typography>
                                    <Chip label={completedTasks.length} size="small" />
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 1 }}>
                                <Stack spacing={1}>
                                    {sortTasks(completedTasks, sortBy).map((task) => (
                                        <TaskCard key={task.id} task={task} onOpen={setPreviewTask} />
                                    ))}
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                    )}
                </>
            )}

            <TaskPreviewDialog
                task={previewTask}
                onClose={() => setPreviewTask(null)}
                currentUserId={currentUser.id}
                onTaskUpdated={handleTaskUpdated}
                onEdit={handleEdit}
                onDeleted={handleDeleted}
            />

            <AddTaskDialog
                open={dialogOpen}
                onClose={() => { setDialogOpen(false); setEditTask(null); }}
                onSaved={handleSaved}
                currentUser={currentUser}
                defaultProject={editTask ? null : project}
                taskLists={taskLists}
                item={editTask}
            />
        </Box>
    );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function Tasks() {
    const { user } = useAuth();
    const [tab, setTab] = useState(0);
    const [taskLists, setTaskLists] = useState([]);
    const [allUsers, setAllUsers] = useState([]);

    useEffect(() => {
        TaskService.listTaskLists({ assignee: user?.id })
            .then((res) => setTaskLists(res.data))
            .catch(() => setTaskLists([]));
        UserService.list()
            .then((res) => {
                const users = res.data.results ?? res.data;
                setAllUsers(users.filter((u) => u.is_active !== false && u.user_type === "employee"));
            })
            .catch(() => setAllUsers([]));
    }, [user?.id]);

    if (!user) return null;

    return (
        <Box>
            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
                variant="fullWidth"
            >
                <Tab label="Today Focus" sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, textTransform: "none" }} />
                <Tab label="My Tasks" sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, textTransform: "none" }} />
                <Tab label="Project Tasks" sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, textTransform: "none" }} />
            </Tabs>

            {tab === 0 && <TodayFocusTab currentUser={user} taskLists={taskLists} />}
            {tab === 1 && <MyTasksTab currentUser={user} taskLists={taskLists} allUsers={allUsers} />}
            {tab === 2 && <ProjectTasksTab currentUser={user} taskLists={taskLists} />}
        </Box>
    );
}
